/**
 * 触觉反馈封装（docs/DEVICES.md R4）。
 *
 * 为什么值得单独一个文件，而不是到处写 `navigator.vibrate?.(20)`：
 *
 *  1. **iOS 上 `navigator.vibrate` 恒不存在**（Safari 与所有 WebView，含微信）。
 *     可选链写法「不会报错」，于是在 Mac 上调试永远发现不了 ——
 *     实测机 D2 就是 iOS + 微信，那台机器上所有震动反馈是**静默失效**的。
 *     既然一半用户根本震不了，就必须有一条明确的降级路径，而不是碰运气。
 *  2. **Android 上 `vibrate()` 会返回 `false`**（无用户手势、页面不可见、系统禁用）。
 *     返回值没人看 = 同样静默失效。这里连续失败三次就永久转降级模式。
 *  3. 降级 = **音效 + 视觉补偿**：按下的元素做一次 120ms 的轻微压缩
 *     （viewport.css 的 `.haptic-pulse`），配一声 40ms 的短音。
 *     震动的作用是「确认这一下点到了」，视觉+听觉能补上同一件事。
 *
 * 全部依赖都可注入，所以这个文件能在 node 里完整测试（没有真 navigator / AudioContext）。
 */
import { resumeAudioContext, sharedAudioContext } from './audioContext.js'

// ============================================================================
// 模式表
// ============================================================================

export type HapticPattern =
  /** 滑块经过档位的磁吸 */
  | 'tick'
  /** 选中一个档位 / 切换开关 */
  | 'select'
  /** 提交动作（弃牌 / 跟注 / 加注） */
  | 'commit'
  /** 轮到你了 */
  | 'turn'
  /** 危险操作（退出牌局、全下确认） */
  | 'warn'
  /** 赢下底池 */
  | 'win'
  /** 被人催了（双脉冲：和「轮到你」的单下、「警告」的长下都分得开） */
  | 'nudge'

/** 震动时长（ms）。全部 <= 40ms：手机上超过 50ms 的震动在牌桌这种高频场景里很烦。 */
export const HAPTIC_MS: Readonly<Record<HapticPattern, number>> = {
  tick: 8,
  select: 12,
  commit: 25,
  turn: 18,
  warn: 40,
  win: 30,
  nudge: 30,
}

/**
 * 需要多段震动的模式。只有「被催」一种：它和「轮到你」（18ms 单下）几乎同时发生，
 * 震感必须一摸就分得出来，否则被催的人会以为自己刚轮到。
 */
export const HAPTIC_SEQUENCE: Readonly<Partial<Record<HapticPattern, readonly number[]>>> = {
  nudge: [30, 70, 30],
}

/**
 * 降级音效的频率（Hz）。控制在中低频，避免滑块拖动时连续出现尖锐蜂鸣。
 * 这组声音只负责补足“按到了”的感觉，不和牌桌事件音争注意力。
 */
export const HAPTIC_HZ: Readonly<Record<HapticPattern, number>> = {
  tick: 440,
  select: 380,
  commit: 260,
  turn: 480,
  warn: 180,
  win: 360,
  nudge: 220,
}

export type HapticMode =
  /** 真震动可用 */
  | 'vibrate'
  /** 震不了，用音效 + 视觉补偿 */
  | 'compensate'
  /** 用户在设置里关了 */
  | 'off'

/** 连续失败多少次判定为「这台设备其实震不了」。1 次就下结论会误伤「刚好没有用户手势」。 */
export const VIBRATE_FAILURE_LIMIT = 3

// ============================================================================
// 能力探测
// ============================================================================

export interface VibrateCapableNavigator {
  vibrate?: (pattern: number | readonly number[]) => boolean
  userAgent?: string
  maxTouchPoints?: number
  platform?: string
}

/**
 * 有没有 `vibrate`。**这是唯一的判据** —— 不拿 UA 当判据：
 * UA 白名单会在下一代设备上过期，而能力检测不会。
 */
export function vibrationSupported(nav: VibrateCapableNavigator | null | undefined): boolean {
  return typeof nav?.vibrate === 'function'
}

/**
 * 是不是 iOS 系（含 iPadOS 的「桌面 UA」伪装）。
 * **只用来决定补偿方式**（iOS 上补偿是刚需），绝不用来决定「能不能震」。
 */
export function isIosLike(nav: VibrateCapableNavigator | null | undefined): boolean {
  const ua = nav?.userAgent ?? ''
  if (/iPad|iPhone|iPod/.test(ua)) return true
  // iPadOS 13+ 默认报 Macintosh，靠触点数区分真 Mac 与 iPad
  return /Macintosh/.test(ua) && (nav?.maxTouchPoints ?? 0) > 1
}

// ============================================================================
// 降级：音效
// ============================================================================

export interface GainLike {
  readonly gain: {
    value: number
    setValueAtTime(value: number, at: number): void
    exponentialRampToValueAtTime(value: number, at: number): void
  }
  connect(dest: unknown): void
  disconnect?(): void
}

export interface OscillatorLike {
  type: string
  readonly frequency: { value: number }
  connect(dest: unknown): void
  start(at?: number): void
  stop(at?: number): void
}

export interface AudioContextLike {
  readonly currentTime: number
  readonly state: string
  readonly destination: unknown
  createOscillator(): OscillatorLike
  createGain(): GainLike
  resume?(): unknown
}

export type AudioContextFactory = () => AudioContextLike | null

// 真正的构造与「全应用只留一个」在 audioContext.ts；这里转出去，旧的导入路径不用改
export { defaultAudioContextFactory } from './audioContext.js'

/**
 * 一声带柔和起音的短音。起点从近乎静音爬升，避免振荡器瞬间从 0 跳到目标音量时
 * 产生“啪”的高频点击；默认音量只给触觉降级使用，牌桌事件音会显式传自己的音量。
 */
export function playBlip(
  ctx: AudioContextLike,
  hz: number,
  ms: number,
  volume = 0.025,
  waveform = 'sine',
  attackMs = 3,
): boolean {
  try {
    // iOS 初始常为 suspended，也可能在音频焦点被打断后进入 interrupted。
    // 触觉调用位于真实手势栈；牌桌音效则会先由 sfx.unlock() 解锁同一个 context。
    // 不在手势里时 resume 可能被拒：吞掉，别留一串 unhandledrejection
    if (ctx.state !== 'running' && ctx.state !== 'closed') void resumeAudioContext(ctx)
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = waveform
    osc.frequency.value = hz
    const t0 = ctx.currentTime
    const dur = Math.max(0.01, ms / 1000)
    const attack = Math.min(dur * 0.35, Math.max(0.001, attackMs / 1000))
    gain.gain.setValueAtTime(0.0001, t0)
    gain.gain.exponentialRampToValueAtTime(volume, t0 + attack)
    // 指数衰减让尾音自然收住，不留第二个点击
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(t0)
    osc.stop(t0 + dur)
    return true
  } catch {
    return false
  }
}

// ============================================================================
// 降级：视觉
// ============================================================================

export interface PulseTarget {
  readonly classList: { add(name: string): void; remove(name: string): void }
}

export const PULSE_CLASS = 'haptic-pulse'
const PULSE_MS = 140

/**
 * 视觉补偿：给被点的元素挂一下 `.haptic-pulse`（120ms 的轻微压缩）。
 * 只动 transform，不改布局（红线 #46）。
 *
 * ★ 不要传舞台上的 `.stage-anchor`（座位块 / 牌）：那些元素身上有
 * `translate(-50%,-50%)` 这条静态变换，动画的 scale 会把它整条替换掉，
 * 元素会先跳半个身位。只给按钮这类没有静态变换的元素用。
 */
export function pulseElement(
  el: PulseTarget | null | undefined,
  timers: HapticTimers = realHapticTimers,
): void {
  if (!el?.classList) return
  el.classList.remove(PULSE_CLASS)
  el.classList.add(PULSE_CLASS)
  timers.setTimeout(() => el.classList.remove(PULSE_CLASS), PULSE_MS)
}

// ============================================================================
// 主体
// ============================================================================

export interface HapticTimers {
  setTimeout(fn: () => void, ms: number): unknown
  clearTimeout(handle: unknown): void
}

export const realHapticTimers: HapticTimers = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
}

export interface HapticsOptions {
  /** 默认 globalThis.navigator */
  readonly nav?: VibrateCapableNavigator | null
  /** 设置里的「震动」开关。默认恒开，由调用方传 settingsStore 的值。 */
  readonly enabled?: () => boolean
  /** 设置里的「音效」开关。关了就只剩视觉补偿。 */
  readonly soundEnabled?: () => boolean
  /** 页面是否可见。不可见时震动会被系统忽略，没必要浪费一次失败计数。 */
  readonly visible?: () => boolean
  readonly audio?: AudioContextFactory
  readonly timers?: HapticTimers
  /** 测试观测点：每次真的调用 vibrate / 音效 / 视觉时回调 */
  readonly onFire?: (info: { pattern: HapticPattern; mode: HapticMode; vibrated: boolean }) => void
}

export interface FireOptions {
  /** 视觉补偿的落点（一般就是被点的那个按钮）。不传就只有音效。 */
  readonly target?: PulseTarget | null
  /** 覆盖默认时长 */
  readonly ms?: number
}

export interface Haptics {
  /** 当前模式。设置关掉时是 'off'。 */
  mode(): HapticMode
  /** 这台设备到底能不能震（与设置无关） */
  capable(): boolean
  /** 触发一次。返回是否**真的震了**（降级路径返回 false）。 */
  fire(pattern: HapticPattern, opts?: FireOptions): boolean
  /** 重新探测（测试 / 设备热插拔用） */
  reset(): void
}

export function createHaptics(options: HapticsOptions = {}): Haptics {
  const timers = options.timers ?? realHapticTimers
  const enabled = options.enabled ?? ((): boolean => true)
  const soundEnabled = options.soundEnabled ?? ((): boolean => true)
  // 默认与牌桌音效共用同一个 AudioContext（audioContext.ts）
  const audioFactory = options.audio ?? sharedAudioContext
  const nav =
    options.nav ?? ((globalThis as { navigator?: VibrateCapableNavigator }).navigator ?? null)

  let failures = 0
  let ctx: AudioContextLike | null = null
  // 构造器不存在才永久降级；一次系统音频焦点切换不该让整局永久静音。
  let audioUnsupported = false

  const visible = options.visible ?? ((): boolean => globalThis.document?.visibilityState !== 'hidden')

  const capable = (): boolean => vibrationSupported(nav) && failures < VIBRATE_FAILURE_LIMIT

  const mode = (): HapticMode => {
    if (!enabled()) return 'off'
    return capable() ? 'vibrate' : 'compensate'
  }

  const compensate = (pattern: HapticPattern, opts: FireOptions | undefined): void => {
    // 视觉先行：它零成本且一定成功，音频可能被系统静音策略挡下
    pulseElement(opts?.target ?? null, timers)
    if (!soundEnabled() || audioUnsupported) return
    ctx ??= audioFactory()
    if (ctx === null) {
      audioUnsupported = true
      return
    }
    const ok = playBlip(ctx, HAPTIC_HZ[pattern], Math.max(24, HAPTIC_MS[pattern] * 1.6))
    if (!ok) ctx = null
  }

  return {
    mode,
    capable,
    reset() {
      failures = 0
      audioUnsupported = false
      ctx = null
    },
    fire(pattern, opts): boolean {
      if (!enabled()) return false
      const ms = opts?.ms ?? HAPTIC_MS[pattern]

      if (capable() && visible()) {
        let ok = false
        try {
          // 能力检测已经保证了 vibrate 存在；这里仍然 try 住，
          // 因为某些 WebView 在跨域 iframe 里调它会抛 SecurityError。
          const seq = opts?.ms === undefined ? HAPTIC_SEQUENCE[pattern] : undefined
          ok = nav?.vibrate?.(seq ?? ms) !== false
        } catch {
          ok = false
        }
        if (ok) {
          failures = 0
          options.onFire?.({ pattern, mode: 'vibrate', vibrated: true })
          return true
        }
        // 返回 false = 系统拒绝了（无手势 / 静音 / 省电）。攒够次数就别再试了。
        failures += 1
      }

      compensate(pattern, opts)
      options.onFire?.({ pattern, mode: 'compensate', vibrated: false })
      return false
    },
  }
}

/**
 * 全应用共用的实例。设置开关由调用方在 `fireHaptic` 里传，
 * 这样 haptics 不依赖 store，测试里不用起 zustand。
 */
let shared: Haptics | null = null

export function sharedHaptics(): Haptics {
  shared ??= createHaptics()
  return shared
}

/** 仅供测试 */
export function __setSharedHaptics(h: Haptics | null): void {
  shared = h
}

/**
 * 一行调用点。`enabled` 直接传 `useSettingsStore` 里的 haptics 开关。
 *
 * 用法：
 * ```ts
 * onPointerDown={(e) => fireHaptic('commit', haptics, e.currentTarget)}
 * ```
 */
export function fireHaptic(
  pattern: HapticPattern,
  enabled: boolean,
  target?: PulseTarget | null,
): boolean {
  if (!enabled) return false
  return sharedHaptics().fire(pattern, { target: target ?? null })
}
